import { createContext, ReactNode, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';

type EntityId = string | number;

type ApiResponse<T = any> = {
  data?: T;
  message?: string;
  status?: number;
  success?: boolean;
};

export interface RoundTrip {
  id_trip: EntityId;
  harvest?: number;
  begin_date?: string | null;
  end_date?: string | null;
  production?: number | null;
  trips_name?: number | string | null;
  fk_boats_id_boat?: EntityId;
  boats_name?: string;
  fishingtypes_name?: string;
  estimated_weight?: number | string | null;
  actual_weight?: number | string | null;
  remuneration_payment_status?: 'NOT_GENERATED' | 'PENDING_PAYMENT' | 'PARTIALLY_PAID' | 'PAID';
  can_pay_remuneration_orders?: boolean;
  actions?: {
    canGenerateEstimatedRemuneration?: boolean;
    generateEstimatedRemunerationBlockedReason?: string | null;
  };
}

export interface Round {
  id_round: EntityId;
  created_at?: string;
  updated_at?: string;
  start_date?: string | null;
  end_date?: string | null;
  estimated_weight?: number | string | null;
  actual_weight?: number | string | null;
  harvest?: number | null;
  round_number?: number | null;
  status?: 'OPEN' | 'CLOSED';
  actions?: {
    can_finalize_actual_remuneration?: boolean;
    finalize_actual_remuneration_blocked_reason?: string | null;
    can_reopen_actual_remuneration?: boolean;
    reopen_actual_remuneration_blocked_reason?: string | null;
  };
  trip_count?: number;
  is_complete?: boolean;
  trips?: RoundTrip[];
}

export type RoundInput = {
  trip_ids: EntityId[];
};

export interface RoundsProviderFilter {
  page?: number;
  pageSize?: number;
  status?: 'OPEN' | 'CLOSED';
  harvest?: number;
  roundNumber?: number;
}

export interface RoundsProviderResponse<T = undefined> {
  success: boolean;
  message: string;
  data?: T;
}

export interface RoundsPaginatedResponse<T> {
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  data: T[];
}

interface RoundsProviderProps {
  children: ReactNode;
}

interface RoundsProviderContextData {
  getRounds: (filter?: RoundsProviderFilter) => Promise<RoundsProviderResponse<RoundsPaginatedResponse<Round>>>;
  getRound: (roundId: EntityId) => Promise<RoundsProviderResponse<Round>>;
  getRoundRemunerationPreview: (roundId: EntityId, phase?: 'ESTIMATED' | 'ACTUAL') => Promise<RoundsProviderResponse<any>>;
  getActualRemuneration: (roundId: EntityId) => Promise<RoundsProviderResponse<any>>;
  createRound: (round: RoundInput) => Promise<RoundsProviderResponse<Round>>;
  replaceRoundTrips: (roundId: EntityId, round: RoundInput) => Promise<RoundsProviderResponse<Round>>;
  finalizeActualRemuneration: (roundId: EntityId) => Promise<RoundsProviderResponse<any>>;
  reopenActualRemuneration: (roundId: EntityId) => Promise<RoundsProviderResponse<any>>;
}

const RoundsProviderContext = createContext<RoundsProviderContextData>(
  {} as RoundsProviderContextData
);

export const roundsRoutes = {
  rounds: '/rounds',
  round: (roundId: EntityId) => `/rounds/${roundId}`,
  remunerationPreview: (roundId: EntityId) => `/rounds/${roundId}/remuneration-preview/by-trip`,
  actualRemuneration: (roundId: EntityId) => `/rounds/${roundId}/actual-remuneration`,
  trips: (roundId: EntityId) => `/rounds/${roundId}/trips`,
  finalizeActualRemuneration: (roundId: EntityId) => `/rounds/${roundId}/actual-remuneration/finalize`,
  reopenActualRemuneration: (roundId: EntityId) => `/rounds/${roundId}/actual-remuneration/reopen`
};

function isTechnicalMessage(message: unknown) {
  if (typeof message !== 'string') return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function translateKnownApiMessage(message: unknown) {
  if (typeof message !== 'string') return undefined;

  const normalizedMessage = message.trim();

  if (/^round not found\.?$/i.test(normalizedMessage)) {
    return 'Rodada de remuneração variável não encontrada.';
  }

  if (/^a round cannot contain duplicate trips\.?$/i.test(normalizedMessage)) {
    return 'A rodada não pode conter viagens repetidas.';
  }

  if (/^one or more trips are invalid, inactive, or belong to another company\.?$/i.test(normalizedMessage)) {
    return 'Uma ou mais viagens são inválidas, inativas ou pertencem a outra empresa.';
  }

  if (/^a round cannot contain more than one trip from the same boat\.?$/i.test(normalizedMessage)) {
    return 'A rodada não pode conter mais de uma viagem do mesmo barco.';
  }

  if (/^a round can only contain trips from the same harvest\.?$/i.test(normalizedMessage)) {
    return 'A rodada pode conter apenas viagens da mesma safra.';
  }

  if (/^one or more trips already belong to another round\.?$/i.test(normalizedMessage)) {
    return 'Uma ou mais viagens já pertencem a outra rodada.';
  }

  if (/^trips cannot be changed after the round actual remuneration has been closed\.?$/i.test(normalizedMessage)) {
    return 'Não é possível alterar as viagens após o fechamento da rodada.';
  }

  if (/^trip .+ cannot be removed from the round because it has generated estimated remuneration\. delete that generation first\.?$/i.test(normalizedMessage)) {
    return 'Esta viagem não pode ser removida da rodada porque possui pedidos de remuneração gerados. Remova-os primeiro.';
  }

  if (/^round actual remuneration is already closed\.?$/i.test(normalizedMessage)) {
    return 'A rodada já está fechada.';
  }

  if (/^round actual remuneration has not been closed\.?$/i.test(normalizedMessage)) {
    return 'A rodada ainda não foi fechada.';
  }

  if (/^round actual remuneration is not closed\.?$/i.test(normalizedMessage)) {
    return 'A rodada não está fechada.';
  }

  if (/^round actual remuneration cannot be reopened because one or more balances have been settled\.?$/i.test(normalizedMessage)) {
    return 'Não é possível reabrir a rodada porque um ou mais saldos já foram utilizados.';
  }

  if (/^every round trip must be closed before finalizing actual remuneration\.?$/i.test(normalizedMessage)) {
    return 'Todas as viagens da rodada devem estar encerradas antes de fechar a rodada.';
  }

  if (/^every round trip must be closed before calculating actual remuneration\.?$/i.test(normalizedMessage)) {
    return 'Todas as viagens da rodada devem estar encerradas antes de calcular a apuração real.';
  }

  if (/^every round trip needs a begin date\.?$/i.test(normalizedMessage)) {
    return 'Informe a data de início de todas as viagens da rodada.';
  }

  if (/^round must contain at least one trip\.?$/i.test(normalizedMessage)) {
    return 'A rodada deve possuir pelo menos uma viagem.';
  }

  if (/^commission rule .+ has no employee\.?$/i.test(normalizedMessage)
    || /^base type .+ is not supported yet\.?$/i.test(normalizedMessage)
    || /^calculation type .+ is not supported\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível calcular a prévia de remuneração. Revise as regras de remuneração cadastradas.';
  }

  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) {
    return 'Você não tem permissão para realizar esta ação.';
  }

  if (/^validation failed\.?$/i.test(normalizedMessage)) {
    return 'Não foi possível validar os dados enviados. Revise as informações e tente novamente.';
  }

  return undefined;
}

function toResponse<T>(response: any, successMessage: string, errorMessage: string): RoundsProviderResponse<T> {
  const success = response?.success !== false && response?.status >= 200 && response?.status < 300;
  const apiMessage = response?.data?.message || response?.message;
  const translatedMessage = translateKnownApiMessage(apiMessage);
  const hasDataProperty = response?.data && Object.prototype.hasOwnProperty.call(response.data, 'data');
  const payload = hasDataProperty ? response.data.data : response?.data;

  return {
    success,
    message: success ? successMessage : translatedMessage || (isTechnicalMessage(apiMessage) ? errorMessage : apiMessage),
    data: success ? payload : undefined
  };
}

function removeEmptyFilters(filter: RoundsProviderFilter) {
  return Object.entries(filter).reduce<Record<string, unknown>>((params, [key, value]) => {
    if (value !== "''" && value !== '' && value !== undefined && value !== null) {
      params[key] = value;
    }
    return params;
  }, {});
}

export function RoundsProvider({ children }: RoundsProviderProps) {
  const queryClient = useQueryClient();

  function invalidateRelatedQueries() {
    queryClient.invalidateQueries({ queryKey: ['trips'] });
  }

  async function getRounds(filter: RoundsProviderFilter = {}) {
    const response: ApiResponse = await apiV2.get(roundsRoutes.rounds, { params: removeEmptyFilters(filter) });
    const apiMessage = response?.data?.message || response?.message;
    const translatedMessage = translateKnownApiMessage(apiMessage);
    const success = response?.success !== false && (response?.status ?? 0) >= 200 && (response?.status ?? 0) < 300;

    return {
      success,
      message: success ? 'Rodadas consultadas com sucesso!' : translatedMessage || (isTechnicalMessage(apiMessage) ? 'Não foi possível consultar as rodadas de remuneração variável.' : apiMessage),
      data: success ? response?.data : undefined
    };
  }

  async function getRound(roundId: EntityId) {
    const response = await apiV2.get(roundsRoutes.round(roundId));
    return toResponse<Round>(response, 'Rodada consultada com sucesso!', 'Não foi possível consultar a rodada de remuneração variável.');
  }

  async function getRoundRemunerationPreview(roundId: EntityId, phase: 'ESTIMATED' | 'ACTUAL' = 'ESTIMATED') {
    const response = await apiV2.get(roundsRoutes.remunerationPreview(roundId), { params: { phase } });
    return toResponse<any>(response, 'Prévia de remuneração consultada com sucesso!', 'Não foi possível consultar a prévia de remuneração da rodada.');
  }

  async function getActualRemuneration(roundId: EntityId) {
    const response = await apiV2.get(roundsRoutes.actualRemuneration(roundId));
    return toResponse(response, 'Apuração real consultada com sucesso!', 'Não foi possível consultar a apuração real da rodada.');
  }

  async function createRound(round: RoundInput) {
    const response = await apiV2.post(roundsRoutes.rounds, round);
    if (response?.status >= 200 && response?.status < 300) invalidateRelatedQueries();
    return toResponse<Round>(response, 'Rodada de remuneração variável adicionada com sucesso!', 'Não foi possível adicionar a rodada de remuneração variável.');
  }

  async function replaceRoundTrips(roundId: EntityId, round: RoundInput) {
    const response = await apiV2.put(roundsRoutes.trips(roundId), round);
    if (response?.status >= 200 && response?.status < 300) invalidateRelatedQueries();
    return toResponse<Round>(response, 'Viagens da rodada alteradas com sucesso!', 'Não foi possível alterar as viagens da rodada.');
  }

  async function finalizeActualRemuneration(roundId: EntityId) {
    const response = await apiV2.post(roundsRoutes.finalizeActualRemuneration(roundId));
    if (response?.status >= 200 && response?.status < 300) invalidateRelatedQueries();
    return toResponse(response, 'Remuneração real fechada com sucesso!', 'Não foi possível fechar a remuneração real da rodada.');
  }

  async function reopenActualRemuneration(roundId: EntityId) {
    const response = await apiV2.post(roundsRoutes.reopenActualRemuneration(roundId));
    if (response?.status >= 200 && response?.status < 300) invalidateRelatedQueries();
    return toResponse(response, 'Apuração real reaberta com sucesso!', 'Não foi possível reabrir a apuração real da rodada.');
  }

  return (
    <RoundsProviderContext.Provider value={{
      getRounds,
      getRound,
      getRoundRemunerationPreview,
      getActualRemuneration,
      createRound,
      replaceRoundTrips,
      finalizeActualRemuneration,
      reopenActualRemuneration
    }}>
      {children}
    </RoundsProviderContext.Provider>
  );
}

export function UseRoundsProvider() {
  return useContext(RoundsProviderContext);
}
